
import React, { useState, useEffect, useContext, useMemo,useCallback } from 'react';
import { Box, Typography, Button, CircularProgress, IconButton, Collapse, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings'; 
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BusinessIcon from '@mui/icons-material/Business';

import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { useQuery } from '@tanstack/react-query';

import AuthContext from './auth/Context';
import WidgetDispatcher from './DynamicWidgetDispatcher';
import useScrollRestoration from './useScrollRestoration';

import DashboardHeader from './DynamicDashboardHeader';
import TimekeepingWidget from './DynamicDashboardTimekeepingWidget';
import WidgetAddDialog from './DynamicWidgetAddDialog';
import WidgetCongTyDialog from './DynamicWidgetCongTyDialog';
import { hoTroNhieuCongTy } from './dashboardCongTy';
import ThanhLocCongTy, { useLocCongTyDashboard } from './DashboardLocCongTy';
import { LocCongTyContext } from './PhamViCongTy';

const ResponsiveGridLayout = WidthProvider(Responsive);

const generateLayoutItem = (w) => {
  let defaultW = 6, defaultH = 15, minH = 5, maxH = Infinity;
  const isListAddAction = w.type === 'list' && w.widgetConfig?.action === 'add';

  if (w.type === 'counter') { defaultW = 3; defaultH = 3; minH = 3; }
  if (w.type === 'shortcut' || isListAddAction) { defaultW = 2; defaultH = 3; minH = 3; }
  // Trang tuỳ biến thường có tiêu đề + vài hàng nội dung -> hẹp quá là vỡ; mở rộng mặc định.
  if (w.type === 'custom') { defaultW = 8; defaultH = 14; minH = 6; }

  const safeNum = (val, fallback) => (val === null || val === undefined || val === '') ? fallback : Number(val);
  return {
    i: w.id, 
    x: safeNum(w.x, 0), y: safeNum(w.y, 9999), w: safeNum(w.w, defaultW), h: safeNum(w.h, defaultH), 
    minH, maxH
  };
};

export default function DynamicDashboard({ 
  code, dfWidgets, onChange, containerSx, contentSx, title, readOnly,
  gridLayoutMode = 'responsive',
  baseCols = {lg:12, md:12, sm:12, xs:12, xxs:6},
  showTimekeeping=false,
  hideHeader = false,
  hideCompanyFilter = false
}) {
  const context = useContext(AuthContext);
  
  const userToken = useMemo(() => context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : ''), [context]);
  const appInfoId = context.appInfo?._id;
  const SYSTEM_WIDGETS = useMemo(() => context.config?.SYSTEM_WIDGETS || [], [context.config?.SYSTEM_WIDGETS]);

  const [dashboardId, setDashboardId] = useState(null);
  const [widgets, setWidgets] = useState([]); 
  const [layouts, setLayouts] = useState({ lg: [], md: [], sm: [], xs: [], xxs: [] }); 
  const [currentActiveLayout, setCurrentActiveLayout] = useState([]);
  
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  // Widget đang chọn "công ty lấy dữ liệu" (widgetConfig.id_apps — xem dashboardCongTy.js)
  const [widgetChonCongTy, setWidgetChonCongTy] = useState(null);

  const [showHeader, setShowHeader] = useState(!hideHeader);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const colsConfig = useMemo(() => {
    if (gridLayoutMode === 'fixed') return { lg: baseCols.lg||12, md: baseCols.lg||12, sm: baseCols.lg||12, xs: baseCols.lg||12, xxs: baseCols.lg||12 };
    return baseCols;
  }, [gridLayoutMode, baseCols]);

  const dashboard_code = useMemo(() => {
    let dCode = code;
    if(!dfWidgets && !code) dCode = context.appInfo?.par?.group?.dashboard_code || context.userInfo?.email || "HOME";
    return dCode ? dCode.toUpperCase() : dCode;
  }, [code, dfWidgets, context.appInfo?.par?.group?.dashboard_code, context.userInfo?.email]);

  // Bộ chọn công ty CHUNG (đầu dashboard) — lọc mọi widget cùng lúc; mặc định công ty đang đăng nhập.
  // Tắt với dashboard nhúng để biên tập (dfWidgets/onChange) và khi trang gọi truyền hideCompanyFilter.
  const locCty = useLocCongTyDashboard({ maDashboard: dashboard_code, batDau: !hideCompanyFilter && !dfWidgets && !onChange && !!dashboard_code });

  const { data: dashboardData, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ['dashboardconfig', appInfoId, dashboard_code],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'dashboardconfig', { limit: 1, condition: { code: dashboard_code } });
      return res?.data || res || [];
    },
    enabled: !!appInfoId && !!userToken && !!dashboard_code,
  });

  useEffect(() => {
    if (dashboardData && dashboardData.length > 0) {
      const configDoc = dashboardData[0];
      setDashboardId(configDoc._id || configDoc.id);
      
      if (configDoc.hide_header !== undefined) {
        setShowHeader(!configDoc.hide_header);
      }

      const loadedWidgets = configDoc.configs || [];
      setWidgets(loadedWidgets);
      const layoutData = loadedWidgets.map(w => generateLayoutItem(w));
      setLayouts({ lg: layoutData, md: layoutData, sm: layoutData, xs: layoutData, xxs: layoutData });
    }
  }, [dashboardData]);

  useEffect(() => {
    if (dfWidgets && dfWidgets.length > 0) {
      setWidgets(dfWidgets);
      const layoutData = dfWidgets.map(w => generateLayoutItem(w));
      setLayouts({ lg: layoutData, md: layoutData, sm: layoutData, xs: layoutData, xxs: layoutData });
    }
  }, [dfWidgets]);

  const isGlobalLoading = !appInfoId || isLoadingDashboard;
  const { scrollRef, saveScroll } = useScrollRestoration({ key: 'main_dashboard', isReady: !isGlobalLoading });

  const handleToggleEdit = useCallback(() => setIsEditMode(prev => !prev), []);
  
  const handleAddWidget = useCallback((widgetData, selectedSize) => {
    const newId = `w_${Date.now()}`;
    const newWidget = { id: newId, ...widgetData };
    setWidgets(prev => [...prev, newWidget]);
    setLayouts(prev => {
      const newLayoutItem = generateLayoutItem(newWidget);
      const isListAddAction = newWidget.type === 'list' && newWidget.widgetConfig?.action === 'add';
      if (selectedSize && !isListAddAction) newLayoutItem.w = selectedSize; 
      return { ...prev, lg: [...(prev.lg || []), newLayoutItem], md: [...(prev.md || []), newLayoutItem] };
    });
    setOpenAddModal(false);
    setIsEditMode(true); 
  }, []);

  const handleRemoveWidget = useCallback((widgetId) => {
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
    setLayouts(prev => {
      const newLayouts = { ...prev };
      Object.keys(newLayouts).forEach(bp => { if (newLayouts[bp]) newLayouts[bp] = newLayouts[bp].filter(l => l.i !== widgetId); });
      return newLayouts;
    });
  }, []);

  // Đổi công ty lấy dữ liệu của một widget — chỉ đổi trong bộ nhớ, bấm "Lưu" mới ghi như mọi thay đổi bố cục.
  const handleSaveCongTy = useCallback((dsCongTy) => {
    const id = widgetChonCongTy?.id;
    if (!id) return;
    setWidgets(prev => prev.map(w => {
      if (w.id !== id) return w;
      const widgetConfig = { ...(w.widgetConfig || {}) };
      if (dsCongTy && dsCongTy.length) widgetConfig.id_apps = dsCongTy;
      else delete widgetConfig.id_apps;
      return { ...w, widgetConfig };
    }));
    setWidgetChonCongTy(null);
  }, [widgetChonCongTy]);

  const onLayoutChange = useCallback((layout, allLayouts) => {
    setCurrentActiveLayout(layout); 
    setLayouts(allLayouts); 
  }, []);
  
  const handleSaveDashboard = useCallback(async () => {
    if(readOnly) return;
    setIsSaving(true);
    try {
      const layoutToSave = currentActiveLayout.length > 0 ? currentActiveLayout : (layouts.lg || []);
      const widgetsToSave = widgets.map(w => {
        const lay = layoutToSave.find(l => l.i === w.id);
        return lay ? { ...w, x: lay.x, y: lay.y, w: lay.w, h: lay.h } : w;
      });

      if(onChange){
        onChange({}, widgetsToSave);
      } else if(dashboard_code) {
        const payload = { 
          configs: widgetsToSave, 
          code: dashboard_code, 
          title: dashboardData?.[0]?.title || title,
          hide_header: !showHeader 
        };
        if (dashboardId) {
          await context.apis.asyncPostList(userToken, 'dashboardconfig', { _id: dashboardId, ...payload }, { require_recaptcha: false });
        } else {
          const res = await context.apis.asyncPostList(userToken, 'dashboardconfig', payload, { require_recaptcha: false });
          const savedData = JSON.parse(res);
          setDashboardId(savedData._id || savedData.id);
        }
      }
      setIsEditMode(false); 
    } catch (e) {
      if (context.alert) context.alert("Lỗi khi lưu Dashboard", null, "error");
    } finally { 
      setIsSaving(false); 
    }
  }, [readOnly, currentActiveLayout, layouts.lg, widgets, onChange, dashboard_code, dashboardData, title, dashboardId, context, userToken, showHeader]);

  const renderedWidgets = useMemo(() => {
    return widgets.map((widget) => (
      <Box key={widget.id} sx={{ bgcolor: 'transparent', '& .react-grid-item': { transition: 'all 200ms ease' }, '&:hover': isEditMode ? { outline: '2px dashed #007aff', borderRadius: 3 } : {} }}>
        {isEditMode && (
          <IconButton 
            size="small" color="error" 
            onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} 
            onClick={() => handleRemoveWidget(widget.id)}
            sx={{ position: 'absolute', top: 4, right: 4, zIndex: 11, bgcolor: 'rgba(255,255,255,0.9)', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
          >
             <CloseIcon fontSize="small" />
          </IconButton>
        )}
        {isEditMode && hoTroNhieuCongTy(widget) && (
          <Tooltip title={(widget.widgetConfig?.id_apps || []).length ? `Lấy dữ liệu của ${widget.widgetConfig.id_apps.length} công ty` : 'Lấy dữ liệu của công ty khác'}>
            <IconButton
              size="small"
              color={(widget.widgetConfig?.id_apps || []).length ? 'primary' : 'default'}
              onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}
              onClick={() => setWidgetChonCongTy(widget)}
              sx={{ position: 'absolute', top: 4, right: 40, zIndex: 11, bgcolor: 'rgba(255,255,255,0.9)', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
            >
              <BusinessIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ height: '100%', opacity: isEditMode ? 0.7 : 1, pointerEvents: isEditMode ? 'none' : 'auto' }}>
          <WidgetDispatcher widget={widget} isEditMode={false} />
        </Box>
      </Box>
    ));
  }, [widgets, isEditMode, handleRemoveWidget]);

  if (isGlobalLoading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f2f2f7', overflow: 'hidden', ...containerSx }}>
      
      {/* KHU VỰC TOP BAR: Chứa cả Header lẫn Nút cấu hình để tránh hiện tượng chồng lấp và che khuất */}
      <Box sx={{ flexShrink: 0, position: 'relative', zIndex: 40, bgcolor: 'white', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        
        {/* Thanh công cụ chỉnh sửa */}
        {!readOnly && (
          <Box
            sx={{
              position: 'absolute',
              top: "12px",
              right: "16px",
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              borderRadius: 8,
              p: 0.5,
              border: '1px solid rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease'
            }}
          >
            <Collapse orientation="horizontal" in={isEditMode} timeout={400}>
              <Box sx={{ display: 'flex', gap: 1, pl: 1, pr: 0.5, alignItems: 'center' }}>
                {isEditMode && !!dashboard_code && !isMobile && <Typography variant="caption" sx={{ pr: 2, whiteSpace: 'nowrap' }}>Code: {dashboard_code}</Typography>}

                {!hideHeader && <Tooltip title={showHeader ? "Ẩn tiêu đề" : "Hiện tiêu đề"}>
                  <Button 
                    size="small"
                    variant="outlined"
                    startIcon={showHeader ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                    onClick={() => setShowHeader(!showHeader)}
                    sx={{ borderRadius: 4, textTransform: 'none', height: 32, whiteSpace: 'nowrap', color: showHeader ? 'primary.main' : 'text.secondary' }}
                  >
                    {showHeader ? "Ẩn tiêu đề" : "Hiện tiêu đề"}
                  </Button>
                </Tooltip>}

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setOpenAddModal(true)}
                  startIcon={<AddIcon />}
                  sx={{ borderRadius: 4, textTransform: 'none', height: 32, whiteSpace: 'nowrap' }}
                >
                  Thêm Widget
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveDashboard}
                  disabled={isSaving}
                  startIcon={<SaveIcon />}
                  sx={{ borderRadius: 4, textTransform: 'none', height: 32, whiteSpace: 'nowrap' }}
                >
                  {isSaving ? 'Lưu...' : 'Lưu'}
                </Button>
              </Box>
            </Collapse>

            <Tooltip title={isEditMode ? "Đóng chỉnh sửa" : "Sửa bố cục Dashboard"}>
              <IconButton
                onClick={handleToggleEdit}
                sx={{
                  width: 36,
                  height: 36,

                  bgcolor: isEditMode ? 'error.lighter' : 'transparent',
                  color: isEditMode ? 'error.main' : 'primary.main',
                  transition: 'transform 0.4s ease',
                  transform: isEditMode ? 'rotate(90deg)' : 'rotate(0deg)',
                  '&:hover': { bgcolor: isEditMode ? 'error.light' : 'rgba(0,0,0,0.04)' }
                }}
              >
                {isEditMode ? <CloseIcon fontSize="small" /> : <SettingsIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Tiêu đề chính Dashboard — nút chọn công ty nằm CÙNG HÀNG, canh phải. Chừa 72px bên phải cho nút
            cài đặt (top 12px, right 16px). Khi đang sửa bố cục, thanh công cụ dài ra phủ vùng này nên ẩn nút đi. */}
        <Collapse in={showHeader}>
          <Box>
            <DashboardHeader
              title={title}
              code={dashboard_code}
              readOnly={true} 
              userName={context.userInfo?.name || 'Khách'}
              sx={{ pr: readOnly ? 3 : 9 }}
              actions={locCty.hien ? (
                <Box sx={{ opacity: isEditMode ? 0 : 1, visibility: isEditMode ? 'hidden' : 'visible', transition: 'opacity .2s', minWidth: 0, display: 'flex' }}>
                  <ThanhLocCongTy {...locCty} gon={isMobile} />
                </Box>
              ) : null}
            />
          </Box>
        </Collapse>

        {/* Ẩn tiêu đề: nút chọn công ty nổi ngay bên trái nút cài đặt, cùng kiểu với thanh công cụ */}
        {!showHeader && locCty.hien && !isEditMode && (
          <Box sx={{ position: 'absolute', top: '12px', right: readOnly ? '16px' : '68px', zIndex: 99, maxWidth: 'calc(100% - 96px)' }}>
            <ThanhLocCongTy {...locCty} gon={isMobile} noi />
          </Box>
        )}
      </Box>

      {/* ===================================================================================== */}
      {/* MAIN CONTENT DASHBOARD (Khu vực cuộn an toàn) */}
      {/* ===================================================================================== */}
      <Box 
        ref={scrollRef} 
        onScroll={saveScroll} 
        sx={{ 
           flexGrow: 1, 
           overflowY: 'auto', 
           overflowX: 'hidden',
           p: 0,
           pt: isEditMode ? 7 : 0, // Đẩy nội dung xuống nếu ở chế độ chỉnh sửa (7 * 8px = 56px)
           transition: 'padding-top 0.3s ease', // Hiệu ứng chuyển động mượt mà
           ...contentSx
        }}
      >
        {!!showTimekeeping && <Box 
          sx={{ 
            bgcolor: '#ffffff', 
            m: 2,
            mb:0,
            p:2,
            borderRadius: 3, 
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
            boxSizing: 'border-box'
          }}
        >
          <TimekeepingWidget />
        </Box>}
        {widgets.length === 0 ? (
          <Box sx={{ textAlign: 'center',m:2, p: 10, color: 'text.secondary', bgcolor: 'white', borderRadius: 3, border: '1px dashed #ccc' }}>
            <Typography variant="h6">Bảng điều khiển đang trống</Typography>
            {!readOnly && (
              <Button variant="contained" onClick={() => {
                setIsEditMode(true)
                setOpenAddModal(true)
              }} sx={{ mt: 2 }}>
                Bắt đầu thiết lập
              </Button>
            )}
          </Box>
        ) : (
          <LocCongTyContext.Provider value={locCty.locCongTy}>
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            onLayoutChange={onLayoutChange}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={colsConfig} 
            rowHeight={20} 
            isDraggable={isEditMode}
            isResizable={isEditMode}
            compactType="vertical" 
            margin={[16, 16]}
          >
            {renderedWidgets}
          </ResponsiveGridLayout>
          </LocCongTyContext.Provider>
        )}
      </Box>

      <WidgetAddDialog 
         open={openAddModal}
         onClose={() => setOpenAddModal(false)}
         onAdd={handleAddWidget}
         systemWidgets={SYSTEM_WIDGETS}
      />

      <WidgetCongTyDialog
         open={!!widgetChonCongTy}
         widget={widgetChonCongTy}
         onClose={() => setWidgetChonCongTy(null)}
         onSave={handleSaveCongTy}
      />
    </Box>
  );
} 